import { useEffect, useState } from 'react'
import { AuthProvider, useAuth } from './lib/AuthContext'
import { LangProvider, useLang } from './lib/LangContext'
import { ThemeProvider } from './lib/ThemeContext'
import Parametres from './pages/Parametres'
import Aide from './pages/Aide'
import { getProduits, getAbonnement, abonnementActif } from './lib/api'
import { registerServiceWorker, syncPendingMouvements, cacheProduits, getProduitsCaches } from './lib/offline'
import { useOnline } from './lib/useOnline'
import { enregistrerMouvement } from './lib/api'
import { getCaissierSession, clearCaissierSession, isCaissierMode } from './lib/caissierSession'
import { getProduitsCaissier } from './lib/apiCaissier'
import AuthPage from './pages/AuthPage'
import Dashboard from './pages/Dashboard'
import Stock from './pages/Stock'
import AjouterProduit from './pages/AjouterProduit'
import Historique from './pages/Historique'
import Facturation from './pages/Facturation'
import Bilan from './pages/Bilan'
import Onboarding from './pages/Onboarding'
import Employes from './pages/Employes'
import Previsions from './pages/Previsions'
import './App.css'

registerServiceWorker()

// ─── VUE CAISSIER ─────────────────────────────────────────
function CaissierApp({ session, onLogout }) {
  const { lang, t, changeLang } = useLang()
  const { isOnline } = useOnline()
  const [tab, setTab] = useState('stock')
  const [produits, setProduits] = useState([])
  const [menuOpen, setMenuOpen] = useState(false)

  useEffect(() => { loadProduits() }, [])

  async function loadProduits() {
    if (isOnline) {
      // Caissier utilise la fonction SECURITY DEFINER (pas de session Auth)
      const { data } = await getProduitsCaissier(session.boutiqueId)
      const prods = data || []
      setProduits(prods)
      cacheProduits(prods)
    } else {
      const cached = await getProduitsCaches()
      setProduits(cached)
    }
  }

  const TABS = [
    { id: 'stock',      icon: '📦', label: t('stock') },
    { id: 'ajouter',   icon: '➕', label: t('add') },
    { id: 'previsions', icon: '📅', label: lang === 'fr' ? 'Prévisions' : 'Forecasts' },
    { id: 'historique', icon: '📋', label: t('history') },
  ]

  return (
    <div style={{ minHeight: '100vh', background: 'var(--cream)' }}>
      <div className="topnav">
        <div className="nav-logo">Stock<span>O</span></div>
        <div className="nav-right">
          {!isOnline && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 50, background: 'var(--amber-pale)', border: '1px solid #E8C080', fontSize: 10, color: '#633806', fontWeight: 500 }}>
              📵 {lang === 'fr' ? 'Hors-ligne' : 'Offline'}
            </div>
          )}
          <div style={{ display: 'flex', gap: 4 }}>
            {['fr','en'].map(code => (
              <button key={code} onClick={() => changeLang(code)} style={{ padding: '4px 8px', borderRadius: 50, fontSize: 10, cursor: 'pointer', border: `1px solid ${lang === code ? 'var(--sage)' : 'var(--cream-dark)'}`, background: lang === code ? 'var(--sage-pale)' : 'transparent', color: lang === code ? 'var(--sage-dark)' : 'var(--muted)', fontWeight: lang === code ? 600 : 400, fontFamily: 'DM Sans, sans-serif' }}>
                {code === 'fr' ? '🇫🇷' : '🇬🇧'}
              </button>
            ))}
          </div>
          {/* Badge caissier */}
          <div style={{ fontSize: 11, color: 'var(--sage)', fontWeight: 500, background: 'var(--sage-pale)', padding: '4px 10px', borderRadius: 50, border: '1px solid var(--sage-light)' }}>
            🧑‍💼 {session.caissierNom}
          </div>
          <button className="nav-btn" onClick={onLogout}>{t('logout')}</button>
        </div>
      </div>

      {/* Nom de la boutique */}
      <div style={{ background: 'var(--warm-white)', borderBottom: '1px solid var(--cream-dark)', padding: '6px 1.25rem', fontSize: 11, color: 'var(--muted)' }}>
        📦 {session.boutiqueNom}
      </div>

      {tab === 'stock'       && <Stock produits={produits} onRefresh={loadProduits} boutiqueId={session.boutiqueId} />}
      {tab === 'ajouter'     && <AjouterProduit onRefresh={loadProduits} boutiqueId={session.boutiqueId} />}
      {tab === 'previsions'  && <Previsions boutiqueId={session.boutiqueId} />}
      {tab === 'historique'  && <Historique boutiqueId={session.boutiqueId} />}

      <div className="bottom-nav">
        {TABS.map(tb => (
          <button key={tb.id} className={`bnav-item ${tab === tb.id ? 'active' : ''}`} onClick={() => setTab(tb.id)}>
            <span className="bnav-icon">{tb.icon}</span>
            <span className="bnav-label">{tb.label}</span>
          </button>
        ))}
      </div>
    </div>
  )
}

// ─── VUE PATRON ───────────────────────────────────────────
function PatronApp() {
  const { user, boutique, boutiqueId, loading, signOut } = useAuth()
  const { t, lang, changeLang } = useLang()
  const { isOnline } = useOnline()
  const [tab, setTab] = useState('dashboard')
  const [menuOpen, setMenuOpen] = useState(false)
  const [produits, setProduits] = useState([])
  const [produitsLoading, setProduitsLoading] = useState(true)
  const [abonnement, setAbonnement] = useState(null)
  const [aboLoading, setAboLoading] = useState(true)
  const [onboardingDone, setOnboardingDone] = useState(false)
  const [syncMsg, setSyncMsg] = useState('')

  useEffect(() => {
    if (user && boutiqueId) {
      loadProduits()
      loadAbonnement()
      const done = localStorage.getItem(`stocko_onboarding_${user.id}`)
      setOnboardingDone(!!done)
    }
  }, [user, boutiqueId])

  useEffect(() => {
    if (isOnline && boutiqueId) {
      syncPendingMouvements(boutiqueId, enregistrerMouvement).then(count => {
        if (count > 0) {
          setSyncMsg(lang === 'fr' ? `✓ ${count} mouvement(s) synchronisé(s)` : `✓ ${count} movement(s) synced`)
          loadProduits()
          setTimeout(() => setSyncMsg(''), 3000)
        }
      })
    }
  }, [isOnline, boutiqueId])

  async function loadProduits() {
    if (isOnline) {
      const { data } = await getProduits(boutiqueId)
      const prods = data || []
      setProduits(prods)
      cacheProduits(prods)
    } else {
      const cached = await getProduitsCaches()
      setProduits(cached)
    }
    setProduitsLoading(false)
  }

  async function loadAbonnement() {
    const { data } = await getAbonnement(boutiqueId)
    setAbonnement(data)
    setAboLoading(false)
  }

  function completeOnboarding() {
    localStorage.setItem(`stocko_onboarding_${user.id}`, '1')
    setOnboardingDone(true)
  }

  function goToTab(t) { setTab(t); setMenuOpen(false) }

  if (loading || aboLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'var(--cream)' }}>
        <div style={{ fontFamily: 'Fraunces, serif', fontSize: 22, color: 'var(--muted)', fontStyle: 'italic' }}>StockO...</div>
      </div>
    )
  }

  if (!onboardingDone) return <Onboarding onComplete={completeOnboarding} onGoToAdd={() => { completeOnboarding(); setTab('ajouter') }} />

  const accesActif = abonnement === null ? true : abonnementActif(abonnement)
  const jours = abonnement ? Math.ceil((new Date(abonnement.date_fin) - new Date()) / 86400000) : 0
  const showWarning = accesActif && jours <= 7 && jours > 0

  const TABS = [
    { id: 'dashboard',   icon: '🏠', label: t('home') },
    { id: 'stock',       icon: '📦', label: t('stock') },
    { id: 'ajouter',     icon: '➕', label: t('add') },
    { id: 'bilan',       icon: '📊', label: t('report') },
    { id: 'more',        icon: '☰',  label: lang === 'fr' ? 'Plus' : 'More' },
  ]

  const MORE_ITEMS = [
    { id: 'previsions',  icon: '📅', label: lang === 'fr' ? 'Prévisions' : 'Forecasts' },
    { id: 'historique',  icon: '📋', label: t('history') },
    { id: 'employes',    icon: '👥', label: lang === 'fr' ? 'Mon équipe' : 'My team' },
    { id: 'facturation', icon: '💳', label: t('subscription') },
    { id: 'parametres',  icon: '⚙️', label: t('settings') },
    { id: 'aide',        icon: '❓', label: t('help') },
  ]

  const isMoreTab = MORE_ITEMS.some(m => m.id === tab)

  return (
    <div style={{ minHeight: '100vh', background: 'var(--cream)' }}>
      <div className="topnav">
        <div className="nav-logo">Stock<span>O</span></div>
        <div className="nav-right">
          {!isOnline && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 50, background: 'var(--amber-pale)', border: '1px solid #E8C080', fontSize: 10, color: '#633806', fontWeight: 500 }}>
              📵 {lang === 'fr' ? 'Hors-ligne' : 'Offline'}
            </div>
          )}
          <div style={{ display: 'flex', gap: 4 }}>
            {['fr','en'].map(code => (
              <button key={code} onClick={() => changeLang(code)} style={{ padding: '4px 8px', borderRadius: 50, fontSize: 10, cursor: 'pointer', border: `1px solid ${lang === code ? 'var(--sage)' : 'var(--cream-dark)'}`, background: lang === code ? 'var(--sage-pale)' : 'transparent', color: lang === code ? 'var(--sage-dark)' : 'var(--muted)', fontWeight: lang === code ? 600 : 400, fontFamily: 'DM Sans, sans-serif' }}>
                {code === 'fr' ? '🇫🇷' : '🇬🇧'}
              </button>
            ))}
          </div>
          <div className="nav-store-name">{boutique?.nom || '—'}</div>
          <button className="nav-btn" onClick={signOut}>{t('logout')}</button>
        </div>
      </div>

      {syncMsg && (
        <div style={{ background: 'var(--sage-pale)', borderBottom: '1px solid var(--sage-light)', padding: '8px 1.25rem', fontSize: 12, color: 'var(--sage-dark)', fontWeight: 500, textAlign: 'center' }}>
          {syncMsg}
        </div>
      )}

      {showWarning && (
        <div style={{ background: 'var(--amber-pale)', borderBottom: '1px solid #E8C080', padding: '10px 1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 12, color: '#633806' }}>⚠ {t('expires_in')} <strong>{jours} {jours > 1 ? t('days') : t('day')}</strong></div>
          <button onClick={() => goToTab('facturation')} style={{ fontSize: 11, color: 'var(--amber)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }}>{t('renew')}</button>
        </div>
      )}

      {menuOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 150 }} onClick={() => setMenuOpen(false)}>
          <div style={{ position: 'absolute', bottom: 65, left: 0, right: 0, background: 'var(--warm-white)', borderTop: '1px solid var(--cream-dark)', borderRadius: '20px 20px 0 0', padding: '1rem', boxShadow: '0 -4px 24px rgba(0,0,0,0.1)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ width: 36, height: 4, background: 'var(--cream-dark)', borderRadius: 2, margin: '0 auto 1rem' }} />
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.75rem' }}>
              {lang === 'fr' ? 'Plus de fonctionnalités' : 'More features'}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {MORE_ITEMS.map(item => (
                <button key={item.id} onClick={() => goToTab(item.id)} style={{ padding: '14px 12px', borderRadius: 'var(--radius-md)', cursor: 'pointer', border: `1.5px solid ${tab === item.id ? 'var(--sage)' : 'var(--cream-dark)'}`, background: tab === item.id ? 'var(--sage-pale)' : 'var(--cream)', display: 'flex', alignItems: 'center', gap: 10, fontFamily: 'DM Sans, sans-serif', transition: 'all 0.15s' }}>
                  <span style={{ fontSize: 22 }}>{item.icon}</span>
                  <span style={{ fontSize: 13, fontWeight: 500, color: tab === item.id ? 'var(--sage-dark)' : 'var(--charcoal)' }}>{item.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {!accesActif && tab !== 'facturation' ? (
        <div className="main" style={{ textAlign: 'center', paddingTop: '3rem' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
          <div style={{ fontFamily: 'Fraunces, serif', fontSize: 22, marginBottom: 8 }}>{t('suspended_title')}</div>
          <div style={{ fontSize: 14, color: 'var(--muted)', marginBottom: '1.5rem', lineHeight: 1.7, whiteSpace: 'pre-line' }}>{t('suspended_desc')}</div>
          <button className="btn-primary" style={{ maxWidth: 280, margin: '0 auto' }} onClick={() => goToTab('facturation')}>{t('see_payment')}</button>
        </div>
      ) : (
        <>
          {tab === 'dashboard'   && <Dashboard produits={produits} produitsLoading={produitsLoading} />}
          {tab === 'stock'       && <Stock produits={produits} onRefresh={loadProduits} />}
          {tab === 'ajouter'     && <AjouterProduit onRefresh={loadProduits} />}
          {tab === 'bilan'       && <Bilan />}
          {tab === 'historique'  && <Historique />}
          {tab === 'facturation' && <Facturation />}
          {tab === 'employes'    && <Employes />}
          {tab === 'parametres'  && <Parametres />}
          {tab === 'aide'        && <Aide />}
          {tab === 'previsions'  && <Previsions />}
        </>
      )}

      <div className="bottom-nav">
        {TABS.map(tb => {
          const isMore = tb.id === 'more'
          const isActive = isMore ? (menuOpen || isMoreTab) : tab === tb.id
          return (
            <button key={tb.id} className={`bnav-item ${isActive ? 'active' : ''}`}
              onClick={() => { if (isMore) setMenuOpen(m => !m); else { setMenuOpen(false); setTab(tb.id) } }}>
              <span className="bnav-icon">{tb.icon}</span>
              <span className="bnav-label">{tb.label}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ─── ROOT ─────────────────────────────────────────────────
function AppContent() {
  const { user, loading } = useAuth()
  const [caissierSession, setCaissierSession] = useState(getCaissierSession())

  function handleCaissierLogin() {
    setCaissierSession(getCaissierSession())
  }

  function handleCaissierLogout() {
    clearCaissierSession()
    setCaissierSession(null)
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'var(--cream)' }}>
        <div style={{ fontFamily: 'Fraunces, serif', fontSize: 22, color: 'var(--muted)', fontStyle: 'italic' }}>StockO...</div>
      </div>
    )
  }

  // Mode caissier PIN
  if (caissierSession) return <CaissierApp session={caissierSession} onLogout={handleCaissierLogout} />

  // Pas connecté
  if (!user) return <AuthPage onCaissierLogin={handleCaissierLogin} />

  // Patron connecté
  return <PatronApp />
}

export default function App() {
  return (
    <ThemeProvider>
      <LangProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </LangProvider>
    </ThemeProvider>
  )
}
